import React, { useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";
import {
  Navigate,
  generatePath,
  useParams,
  useSearchParams,
} from "react-router";
import { ReportContext, SDLCReport } from "@sscs/report/report";
import { ExportReportType, isErrorMessage } from "@sscs/types";
import { ErrorBoundary, Page, PageNotFound } from "@sscs/ui";
import { buildRlPurl } from "@sscs/utils";
import { CommunityVersions } from "Components";
import {
  FULL_REPORT_PATH,
  LOGIN_ROUTE,
  REPORT_EXPORT_FILE_NAME,
  SHAREABLE_REPORT_ROOT_PATH,
  SHARED_REPORT_NOT_FOUND_ROUTE,
} from "Constants";
import { useIsAuthenticated } from "Hooks";
import {
  downloadFile,
  notificationsForReportDownload,
  useGetReportSummaryQuery,
} from "Services";
import { generateReportSearchParams } from "Utils";
import PublicReportBanner from "../../components/PublicReportBanner/PublicReportBanner";
import ReportPasswordForm from "../../components/ReportPasswordForm/ReportPasswordForm";
import SharingStatusText from "../../components/SharingStatusText/SharingStatusText";
import { useGetSharedReportIdsQuery } from "../../services/sharedReports.api";
import styles from "./publicReport.module.scss";

const REPORT_EXPORT_ENDPOINT: Record<ExportReportType, string> = {
  cve: "rl-cve",
  cycloneDX: "cyclone-dx",
  json: "rl-json",
  safe: "rl-safe",
  sarif: "sarif",
  spdx: "spdx",
  summaryPdf: "rl-summary-pdf",
  uri: "rl-uri",
};

const getReportRoute = ({
  groupId,
  projectName,
  packageName,
  version,
  isRepro,
}: {
  groupId: string;
  projectName: string;
  packageName: string;
  version: string;
  isRepro: boolean;
}): string => {
  const path = generatePath(`/${FULL_REPORT_PATH}`, {
    groupId,
    projectName,
    packageName,
    version,
  });

  const searchParams = generateReportSearchParams({ isRepro });
  const reportLink = `${path}?${searchParams}`;

  return reportLink;
};

const PublicReport = (): React.JSX.Element | null => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const fingerprint = searchParams.get("fp");

  const auth = useIsAuthenticated();

  const {
    data: report,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetSharedReportIdsQuery(
    token
      ? {
          token_key: token,
          fingerprint: fingerprint ?? "",
        }
      : skipToken,
  );

  const {
    data: reportSummaryData,
    isError: isReportSummaryError,
    error: reportSummaryError,
    refetch: refetchReportSummary,
  } = useGetReportSummaryQuery(
    report?.latest_report_id && report?.file_info?.file_type === "version"
      ? {
          diff_report_id: report.latest_diff_report_id ?? "",
          ui_id: report.latest_report_id,
        }
      : skipToken,
  );

  const [activeExport, setActiveExport] = useState<
    Record<ExportReportType, boolean>
  >({
    cve: false,
    cycloneDX: false,
    json: false,
    safe: false,
    sarif: false,
    spdx: false,
    summaryPdf: false,
    uri: false,
  });

  if (isError) {
    if ("status" in error && error.status === 404) {
      return <Navigate to={`/${SHARED_REPORT_NOT_FOUND_ROUTE}`} replace />;
    } else if ("data" in error && isErrorMessage(error.data)) {
      return (
        <PageNotFound
          title="Something went wrong"
          byline={error.data.data.error}
        />
      );
    } else {
      return <PageNotFound title="Something went wrong" />;
    }
  }

  if (isLoading || isFetching)
    return (
      <Page.Root>
        <Page.Body status={{ status: "LOADING" }} />
      </Page.Root>
    );

  if (!token || !report || !report?.shared_report || !report?.latest_report_id)
    return null;

  const passwordInputRequired =
    (report.shared_report?.settings.password_protected ?? false) &&
    isReportSummaryError &&
    isErrorMessage(reportSummaryError) &&
    "status" in reportSummaryError &&
    reportSummaryError.status === 401;

  const isRepro = report.shared_report.report_type === "repro";

  const handleReportExport = (reportType: ExportReportType) => {
    setActiveExport((prevState) => ({ ...prevState, [reportType]: true }));
    downloadFile(
      `/reports/v1/exports/${report.latest_report_id}/${REPORT_EXPORT_ENDPOINT[reportType]}`,
      REPORT_EXPORT_FILE_NAME[reportType],
      notificationsForReportDownload(reportType),
    ).then(() => {
      setActiveExport((prevState) => ({ ...prevState, [reportType]: false }));
    });
  };

  return (
    <div
      className={styles["public-report"]}
      // The report shell owns the pane and scrolls its own columns; the
      // password prompt scrolls inside the content pane instead.
      data-page-shell={passwordInputRequired ? undefined : true}
    >
      <ErrorBoundary>
        <ReportContext.Provider
          value={{
            routerRootPath: SHAREABLE_REPORT_ROOT_PATH,
            reportId: report.latest_report_id,
            diffId: report.latest_diff_report_id ?? "",
            selectedCheck: isRepro ? "scan-repro" : "scan-version",
            roles: {
              canDownloadPackage:
                report.shared_report.settings.package_download_enabled,
              canExportCve: report.shared_report.settings.rl_cve_export_enabled,
              canExportCycloneDx:
                report.shared_report.settings.cyclonedx_export_enabled,
              canExportJson:
                report.shared_report.settings.rl_json_export_enabled,
              canExportSafe:
                report.shared_report.settings.rl_safe_export_enabled,
              canExportSarif:
                report.shared_report.settings.sarif_export_enabled,
              canExportSpdx: report.shared_report.settings.spdx_export_enabled,
              canExportSummaryPdf:
                report.shared_report.settings.rl_summary_pdf_export_enabled,
              canExportUri: report.shared_report.settings.rl_uri_export_enabled,
            },
            activeExport,
            supportsYaraPolicies: false,
            onReportExport: handleReportExport,
            CommunityVersions: CommunityVersions,
          }}
        >
          <PublicReportBanner
            loginTo={
              auth.isAuthenticated
                ? getReportRoute({
                    groupId: report.group_id,
                    projectName: report.file_info.project_name,
                    packageName: report.file_info.package_name,
                    version: report.file_info.version,
                    isRepro,
                  })
                : `/${LOGIN_ROUTE}`
            }
            exportedVersion={
              passwordInputRequired
                ? null
                : { versionId: report.file_id, isRepro }
            }
          >
            <SharingStatusText
              sharedBy={report.shared_report.organization_name}
              expirationDate={
                passwordInputRequired
                  ? ""
                  : report.shared_report.settings.expiration_date
              }
              purl={buildRlPurl(
                report.file_info.project_name,
                report.file_info.package_name,
                report.file_info.version,
                isRepro ? "repro" : "version",
              )}
            />
          </PublicReportBanner>
          {passwordInputRequired && (
            <ReportPasswordForm
              token={token}
              onSuccessfulLogin={refetchReportSummary}
            />
          )}
          {!passwordInputRequired && reportSummaryData && <SDLCReport />}
        </ReportContext.Provider>
      </ErrorBoundary>
    </div>
  );
};

export default PublicReport;
